import React, { forwardRef, useState } from 'react'
import { getDateString } from '..'
import { Card } from '../../../atoms'
import { RichText } from '../../helpers/react'
import { markdownParser } from '../../../utils/markdown-parser'
import { LenderIcon } from './LenderIcon'

type RatesListProps = {
  children: React.ReactNode
  footnotes: string
  dateString: string | null
}

type RatesListItemProps = {
  children: React.ReactNode
}

export type Row = {
  type: 'th' | 'td'
  value: string
  marks: any
}

export type RatesTableProps = {
  title?: string
  contents: Row[][]
  footnotes: any
  displayDate: boolean
  localDates: Date
  globalDates: Date
}

export type Rate = {
  initialTermInYears: string
  ltv: string
  lenderName: string
  InitialMonthlyPayment: string
  InitialPayRate: string
  StandardVariableRate: string
  FeesTotal: string
  repExample: string
  Cashback: string
  displayCta: string
  tab: string | undefined
  rateType: string | undefined
  aprc: number | undefined
  overallInitialCost: number | undefined
  ProductName: string
}

export const RatesList = ({ children, footnotes, dateString }: RatesListProps) => {
  return (
    <div className='RatesList'>
      <div className='RatesList__container'>
        <ul aria-label="Mortgage products' rates" className='RatesList__list'>
          {children}
        </ul>
      </div>
      {dateString && <div className='RatesTable__date'>Date {dateString}</div>}
      {footnotes && (
        <div className='RatesTable__footnotes'>
          <RichText content={footnotes} />
        </div>
      )}
    </div>
  )
}

export const RatesListItem = ({ children }: RatesListItemProps) => (
  <li className='RatesTable__listItem'>{children}</li>
)

export type RatesCardProp = React.HTMLAttributes<HTMLDivElement> & {
  product: Rate
  clickout: string
  selectedTab: number
  selectableRates?: Rate[]
};

export const RatesCard = forwardRef<HTMLDivElement, RatesCardProp>(
  (
    {
      product,
      clickout,
      selectedTab,
      className,
      ...props
    }: RatesCardProp,
    // ref,
  ) => {
  const {
    ltv,
    initialTermInYears,
    lenderName,
    InitialMonthlyPayment: monthlyPayment,
    InitialPayRate: initialInterestRate,
    StandardVariableRate: variableRate,
    FeesTotal,
    repExample,
    Cashback,
    displayCta,
    rateType,
    aprc,
  } = product

  const formatNumberThousands = (value: number | string | null) => {
    if (value === null || isNaN(Number(value))) {
      return ''
    }

    const num = Number(value)
    const [integerPart, fractionalPart] = num.toString().split('.')
    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    const formattedFractional = fractionalPart ? fractionalPart.padEnd(2, '0') : ''

    return formattedFractional ? `${formattedInteger}.${formattedFractional}` : formattedInteger
  }

  const cashBack = Number(Cashback)

  return (
    <div
      id={`panel-${selectedTab}`}
      className={`RatesCard__wrapper ${className}`}
      role='tabpanel'
      aria-labelledby={`tab-${selectedTab}`}
      {...props}
    >
      <Card variant='rounded'>
        <div className='RatesCard__headline'>
          <div className='RatesCard__lenderIcon'>
            <LenderIcon lenderName={lenderName ?? ''} />
          </div>
          <ul aria-label='Mortgage product highlights' className='InfoPill__List'>
            <li aria-label='Initial mortgage term' className='InfoPill'>
              {initialTermInYears} years
            </li>
            {rateType && (
              <li aria-label={rateType} className='InfoPill__blue'>
                {rateType} rate
              </li>
            )}
            {cashBack > 0 && (
              <li
                aria-label={`Cashback of £ ${formatNumberThousands(Cashback)}`}
                className='InfoPill__red'
              >
                £ {formatNumberThousands(Cashback)} cashback
              </li>
            )}
          </ul>
        </div>
        <div className='RateTableProduct__wrapper'>
          <ul aria-label='Mortgage product details' className='RateTableProduct__item_list'>
            {monthlyPayment && (
              <li className='RateTableProduct__item_other RateTableProduct__item_highlighted'>
                <span className='RateTableProduct__item_label Product__item_monthly_payment_value'>
                  Monthly repayment
                </span>
                <span className='RateTableProduct__item_value'>
                  £ {formatNumberThousands(monthlyPayment)}
                </span>
              </li>
            )}
            {ltv && (
              <li className='RateTableProduct__item_other'>
                <span className='RateTableProduct__item_label'>Loan to value</span>
                <span className='RateTableProduct__item_value'>{ltv} %</span>
              </li>
            )}
            {initialInterestRate && (
              <li className='RateTableProduct__item_other'>
                <span className='RateTableProduct__item_label'>Initial interest rate</span>
                <span className='RateTableProduct__item_value'>{initialInterestRate} %</span>
              </li>
            )}
            {variableRate && (
              <li className='RateTableProduct__item_other'>
                <span className='RateTableProduct__item_label'>Variable rate</span>
                <span className='RateTableProduct__item_value'>{variableRate} %</span>
              </li>
            )}
            {aprc && (
              <li className='RateTableProduct__item_other'>
                <span className='RateTableProduct__item_label'>APRC</span>
                <span className='RateTableProduct__item_value'>{aprc}%</span>
              </li>
            )}
            {FeesTotal && (
              <li className='RateTableProduct__item_other'>
                <span className='RateTableProduct__item_label'>Product fees</span>
                <span className='RateTableProduct__item_value'>
                  £ {formatNumberThousands(FeesTotal)}
                </span>
              </li>
            )}
          </ul>
          {displayCta === 'true' && (
            <div className='RateTableProduct__cta_wrapper'>
              <a className='RateTableProduct__cta' href={clickout}>
                Find my best mortgage deals
              </a>
            </div>
          )}
        </div>
        <div className='RateTableProduct__representative_example'>
          <span>
            <b>Representative example:</b>
          </span>
          <span dangerouslySetInnerHTML={{ __html: markdownParser(repExample ?? '') }} />
        </div>
      </Card>
    </div>
  )
})
export const formatContents = (contents: Row[][]) => {
  let formattedContents: Rate[] = []
  let tableHeaders: string[] = []

  contents.forEach((row, rowIndex) => {
    let product: Rate = {
      initialTermInYears: '',
      ltv: '',
      lenderName: '',
      InitialMonthlyPayment: '',
      InitialPayRate: '',
      StandardVariableRate: '',
      FeesTotal: '',
      repExample: '',
      Cashback: '',
      displayCta: '',
      tab: '',
      rateType: '',
      aprc: undefined,
      overallInitialCost: undefined,
      ProductName: ''
    }
    row.forEach((cell, cellIndex) => {
      if (cell.type === 'th') {
        tableHeaders.push(cell.value)
      } else {
        if (tableHeaders[cellIndex]) {
          product[tableHeaders[cellIndex]] = cell.value
        }
      }
    })

    if (rowIndex > 0) {
      formattedContents.push(product)
    }
  })

  return formattedContents
}

const RatesTable = (props: RatesTableProps) => {
  const { title, contents, footnotes, displayDate, localDates, globalDates } = props

  const dateString = displayDate ? getDateString(localDates, globalDates) : null

  const formattedContents: Rate[] = formatContents(contents)
  const tabs = Array.from(new Set(formattedContents.map((item) => item.tab)))

  const [selectedTab, setSelectedTab] = useState(0)

  const handleTabChange = (event: React.MouseEvent<HTMLButtonElement>) => {
    setSelectedTab(Number(event.currentTarget.value))
  }

  return (
    <>
      <div className='RateTableWrapper'>
        {title && <div className='RateTable__title'>{title}</div>}
        {tabs.length > 1 && (
          <ul
            role='tablist'
            aria-label='Mortgage products'
            className='RateTableProduct__type_wrapper'
          >
            {tabs.map((tab, idx) => (
              <button
                key={tab}
                className={`RateTableProduct__tabButton ${
                  selectedTab === idx ? 'RateTableProduct__tabButton_active' : ''
                }`}
                role='tab'
                id={`tab-${idx}`}
                aria-label={`Mortgage products ${tab}`}
                aria-selected={selectedTab === idx}
                aria-controls={`panel-${idx}`}
                value={idx}
                onClick={handleTabChange}
              >
                {tab}
              </button>
            ))}
          </ul>
        )}
        <RatesList footnotes={footnotes} dateString={dateString}>
          {formattedContents
            .filter((row) => selectedTab === tabs.indexOf(row.tab))
            .map((row, rowIndex) => (
              <div key={`${selectedTab}-${rowIndex}`}>
                <RatesListItem key={rowIndex}>
                  <RatesCard
                    product={row}
                    clickout={clickouts[rowIndex]}
                    selectedTab={selectedTab}
                  />
                </RatesListItem>
              </div>
            ))}
        </RatesList>
      </div>
    </>
  )
}

export default RatesTable

const clickouts = [
  'https://www.uswitch.com/clk/mortgages/uswitch/mortgages?r=https%3A%2F%2Fuswitch.mojomortgages.com%2Fget-started%3Futm_source%3DPartner%26utm_medium%3DUswitch%26utm_term%3D%7B%7Bclick_ref%7D%7D%26utm_campaign%3Dseo_rates_table_cta1&sig=821b92d719ee097bcbd68dedfed1f369bf8c9faf25985e85c7356eaeb8c2e8d8&brand=uswitch',
  'https://www.uswitch.com/clk/mortgages/uswitch/mortgages?r=https%3A%2F%2Fuswitch.mojomortgages.com%2Fget-started%3Futm_source%3DPartner%26utm_medium%3DUswitch%26utm_term%3D%7B%7Bclick_ref%7D%7D%26utm_campaign%3Dseo_rates_table_cta2&sig=9d5d09a11d841ac5544e86b590ad4f73d779052a108aaf74b6fa6a6051d0f201&brand=uswitch',
  'https://www.uswitch.com/clk/mortgages/uswitch/mortgages?r=https%3A%2F%2Fuswitch.mojomortgages.com%2Fget-started%3Futm_source%3DPartner%26utm_medium%3DUswitch%26utm_term%3D%7B%7Bclick_ref%7D%7D%26utm_campaign%3Dseo_rates_table_cta3&sig=e588ea96ee5dce05ce47e350d18f1d7fb57b0d3e3be93f13e2fbad89808aef9e&brand=uswitch',
  'https://www.uswitch.com/clk/mortgages/uswitch/mortgages?r=https%3A%2F%2Fuswitch.mojomortgages.com%2Fget-started%3Futm_source%3DPartner%26utm_medium%3DUswitch%26utm_term%3D%7B%7Bclick_ref%7D%7D%26utm_campaign%3Dseo_rates_table_cta4&sig=f2b5be9592a64c06e2d83f0a86cebc2fd946e6bf151d6aed22c05eb9b665d912&brand=uswitch',
  'https://www.uswitch.com/clk/mortgages/uswitch/mortgages?r=https%3A%2F%2Fuswitch.mojomortgages.com%2Fget-started%3Futm_source%3DPartner%26utm_medium%3DUswitch%26utm_term%3D%7B%7Bclick_ref%7D%7D%26utm_campaign%3Dseo_rates_table_cta5&sig=2f87253fa2a6de195ee7346a13136653709090aeae743f42522fb61fa9db60a3&brand=uswitch',
  'https://www.uswitch.com/clk/mortgages/uswitch/mortgages?r=https%3A%2F%2Fuswitch.mojomortgages.com%2Fget-started%3Futm_source%3DPartner%26utm_medium%3DUswitch%26utm_term%3D%7B%7Bclick_ref%7D%7D%26utm_campaign%3Dseo_rates_table_cta6&sig=4911265603945a842ae6a25dffe49729b099d6bc60c1ca3d8bb3463ad58cbd82&brand=uswitch',
  'https://www.uswitch.com/clk/mortgages/uswitch/mortgages?r=https%3A%2F%2Fuswitch.mojomortgages.com%2Fget-started%3Futm_source%3DPartner%26utm_medium%3DUswitch%26utm_term%3D%7B%7Bclick_ref%7D%7D%26utm_campaign%3Dseo_rates_table_cta7&sig=d050cca80cc324686bd0ce0d190c11077f3f299b3ccdbd2fb5e41726ef836a30&brand=uswitch',
  'https://www.uswitch.com/clk/mortgages/uswitch/mortgages?r=https%3A%2F%2Fuswitch.mojomortgages.com%2Fget-started%3Futm_source%3DPartner%26utm_medium%3DUswitch%26utm_term%3D%7B%7Bclick_ref%7D%7D%26utm_campaign%3Dseo_rates_table_cta8&sig=c6b19f955d3aeab72cd4563b351709dbdc369370bf339c73ac904f8ea8fd6ee7&brand=uswitch',
]
